import { useMutation } from '@apollo/client'
import { Box, FormModal, Link, Text } from '@chaine/keychaine'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'

import { Toast } from '../../../_shared/components/toast'
import { validations } from '../../../_utils/validations/validations'
import { AppModalProps } from '../components/apps-data'
import { ENABLE_TIVE_APP, EnableTiveAppInput } from '../data/mutations'

/**
 * Saves Tive app's client ID and client secret.
 *
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 */
const TiveModal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, refetchApps } = props
  const { showToast } = Toast()
  const [formError, setFormError] = useState('')

  const [enableTiveApp, { loading }] = useMutation<
    { enableTiveApp: { clientID: string; clientSecret: string } },
    EnableTiveAppInput
  >(ENABLE_TIVE_APP, {
    onCompleted: () => {
      showToast({
        status: 'success',
        title: `Tive app is enabled successfully!`
      })
      refetchApps()
      onClose()
      setFormError('')
    },
    onError: (err) => {
      if (err.message === 'Incorrect client ID or client secret') {
        setFormError('Invalid Client ID/Client Secret. Please try again.')
        showToast({
          status: 'error',
          title: `Invalid Client ID/Client Secret. Please try again.`
        })
      } else {
        setFormError(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
        showToast({
          status: 'error',
          title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        })
      }
    }
  })

  const closeModal = () => {
    setFormError('')
    onClose()
  }

  type FieldValues = { clientID: string; clientSecret: string }
  const handleSaveDetails: SubmitHandler<FieldValues> = (event) => {
    enableTiveApp({
      variables: {
        appType,
        clientID: event.clientID,
        clientSecret: event.clientSecret
      }
    })
  }

  const footer = (
    <Box>
      <Text color="red.600">{formError}</Text>
      <Text>
        {`See `}
        <Link
          color="blue.600"
          href="https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/tive"
          target="_blank"
          rel="noopener noreferrer"
        >
          detailed steps here.
        </Link>
      </Text>
    </Box>
  )

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={closeModal}
        title={`Enable ${appType} for Chaine`}
        subtitle={`Enabling this integration requires an admin Tive account. Enter your Tive Client ID and Client Secret.`}
        formSubmitHandler={handleSaveDetails}
        buttonText={'Enable'}
        initialValues={{}}
        validations={validations}
        isLoading={loading}
        footer={formError ? footer : <></>}
        modalItems={[
          {
            labelText: 'Tive Client ID',
            name: 'clientID',

            placeHolder: 'Enter your Tive client ID',
            type: 'input'
          },
          {
            labelText: 'Tive Client Secret',
            name: 'clientSecret',

            placeHolder: 'Enter your Tive client Secret',
            type: 'input'
          }
        ]}
      />
    </>
  )
})

TiveModal.displayName = 'TiveModal'

export { TiveModal }
